import { useExemptCreator, useUnblockCreator } from "@/api/blocklist";
import { Button } from "@/components/base/buttons/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { error, success } from "@/providers/ToastProvider";

interface UnblockCreatorModalProps {
  creatorOpenId: string;
  creatorName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UnblockCreatorModal({ creatorOpenId, creatorName, open, onOpenChange }: UnblockCreatorModalProps) {
  const unblock = useUnblockCreator();
  const exempt = useExemptCreator();
  const busy = unblock.isPending || exempt.isPending;

  const handleUnblock = async () => {
    try {
      await unblock.mutateAsync(creatorOpenId);
      success(`${creatorName} has been unblocked successfully.`);
      onOpenChange(false);
    } catch {
      error("Failed to unblock creator");
    }
  };

  const handleExempt = async () => {
    try {
      await exempt.mutateAsync(creatorOpenId);
      success(`${creatorName} has been unblocked and exempted from rules successfully.`);
      onOpenChange(false);
    } catch {
      error("Failed to unblock creator");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader className="pt-2">
          <DialogTitle className="mb-3">Unblock {creatorName}?</DialogTitle>
          <DialogDescription className="mb-5">
            They'll appear in your recommendations, search, and imports again. A rule may re-block them on your next
            import, choose 'Unblock and exempt from rules' to keep them exempt.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-row items-center justify-between gap-2 sm:justify-between">
          <Button color="secondary" size="md" isDisabled={busy} onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <div className="flex gap-2">
            <Button color="secondary" size="md" isDisabled={busy} onClick={handleUnblock}>
              Unblock
            </Button>
            <Button color="primary" size="md" isDisabled={busy} onClick={handleExempt}>
              Unblock and exempt from rules
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
